import styles from "./BankTransferModal.module.scss";
import iconStyles from "../Icon.module.scss";
import buttonStyles from "../Button.module.scss";
import cardStyles from "../Card.module.scss";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import tabStyles from "../Tab.module.scss";

export interface BankTransferModalHandle {
  onOpen: () => void;
}

interface TransferData {
  name: string;
  iban: string;
  bic: string;
  address: string;
}

interface TransferDataUSD {
  name: string;
  accountType: string;
  routingNumber: string;
  accountNumber: string;
  address: string;
  bic: string;
}

type BankTransferData = TransferData | TransferDataUSD;

const BANK_TRANSFER_DATA: Record<string, BankTransferData> = {
  EUR: {
    name: "International Reformed Evangelical Church Stockholm",
    iban: "BE75 9056 6559 9051",
    bic: "TRWIBEB1XXX",
    address: "Wise, Rue du Trône 100, 3rd floor, Brussels, 1050, Belgium",
  },
  SEK: {
    name: "International Reformed Evangelical Church Stockholm",
    iban: "GB68 TRWI 6084 6417 3841 49",
    bic: "TRWIGB2LXXX",
    address:
      "Wise Payments Limited, Worship Square, 65 Clifton Street, London, EC2A 4JE, United Kingdom",
  },
  USD: {
    name: "International Reformed Evangelical Church Stockholm",
    accountType: "Deposit",
    routingNumber: "084009519",
    accountNumber: "649651711104963",
    address: "Wise US Inc, 108 W 13th St, Wilmington, DE, 19801, United States",
    bic: "TRWIUS35XXX",
  },
};

const BankTransferModal = forwardRef<BankTransferModalHandle>((_, ref) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const [activeTab, setActiveTab] = useState<string>(
    Object.keys(BANK_TRANSFER_DATA)[0],
  );
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const transferData = BANK_TRANSFER_DATA[activeTab];

  useEffect(() => {
    document.body.classList.toggle("stop", isOpen);

    return () => {
      document.body.classList.remove("stop");
    };
  }, [isOpen]);

  const onOpen = () => {
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const handleCopy = async (field: string, value: string) => {
    await navigator.clipboard.writeText(value);
    setCopiedField(field);
    window.setTimeout(() => setCopiedField(null), 1500);
  };

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    onOpen,
  }));

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  return (
    <div
      ref={modalRef}
      className={`${styles.modal} ${isOpen ? styles.active : ""}`}
      style={{
        display: isOpen ? "block" : "none",
      }}
      onClick={handleBackdropClick}
    >
      <div
        ref={dialogRef}
        data-lenis-prevent
        className={`${styles.dialog} ${isOpen ? styles.active : ""}`}
        style={{
          opacity: isOpen ? 1 : 0,
        }}
      >
        <div className={styles.text} onWheel={(e) => e.stopPropagation()}>
          <div className={styles.desc}>
            <div className={styles.title}>
              <h3>Bank transfer</h3>
            </div>
          </div>

          <div className={`${cardStyles.card} ${styles.card}`}>
            <div className={`${styles.desc} ${styles.subtitle}`}>Via Wise</div>
            <div className={`${styles.desc} ${styles.info}`}>Wise tag</div>
            <div className={`${styles.desc} ${styles.value}`}>
              @internationalreformedevangelicalchurchs2
              <button
                type="button"
                className={styles["copy-button"]}
                aria-label={`Copy Wise`}
                title={`Copy Wise`}
                onClick={() =>
                  handleCopy(
                    "wise",
                    "@internationalreformedevangelicalchurchs2",
                  )
                }
              >
                <span
                  className={`${iconStyles.icon} ${iconStyles.copy}`}
                ></span>
              </button>
            </div>
            <a
              href="https://wise.com/pay/r/oBCJOdpDuQhuGX0"
              className={`${buttonStyles.button}`}
              target="_blank"
              rel="noreferrer"
            >
              Open in Wise
              <i
                className={`${iconStyles.icon} ${styles.icon} ${iconStyles.launch}`}
              ></i>
            </a>
          </div>

          <div className={`${styles.desc} ${styles.subtitle}`}>
            Bank transfer
          </div>

          <div className={tabStyles["tab-group"]}>
            {Object.keys(BANK_TRANSFER_DATA).map((name) => (
              <div
                key={name}
                className={`${tabStyles.tab} ${
                  activeTab === name ? tabStyles.active : ""
                }`}
                onClick={() => setActiveTab(name)}
              >
                {name}
              </div>
            ))}
          </div>

          <div className={styles.wrapper}>
            {("iban" in transferData
              ? [
                  ["Name", "name"],
                  ["IBAN", "iban"],
                  ["Swift / BIC", "bic"],
                  ["Address", "address"],
                ]
              : [
                  ["Name", "name"],
                  ["Account type", "accountType"],
                  ["Routing number", "routingNumber"],
                  ["Account number", "accountNumber"],
                  ["Address", "address"],
                  ["Swift / BIC", "bic"],
                ]
            ).map(([label, field]) => {
              const value = (transferData as unknown as Record<string, string>)[
                field
              ];

              return (
                <div className={styles.row} key={field}>
                  <div className={`${styles.desc} ${styles.info}`}>
                    <p>{label}</p>
                  </div>
                  <div className={`${styles.desc} ${styles.value}`}>
                    <p>{value}</p>
                    <button
                      type="button"
                      className={styles["copy-button"]}
                      aria-label={`Copy ${label}`}
                      title={copiedField === field ? "Copied" : `Copy ${label}`}
                      onClick={() => handleCopy(field, value)}
                    >
                      <span
                        className={`${iconStyles.icon} ${iconStyles.copy}`}
                      ></span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div
          className={`${buttonStyles.btn} ${styles.active} ${styles["close-btn"]}`}
          onClick={onClose}
        >
          <span
            className={`${iconStyles.icon} ${styles.icon} ${iconStyles.close}`}
          ></span>
        </div>
        <div
          className={`${styles.notification} ${copiedField ? styles.visible : ""}`}
          role="status"
          aria-live="polite"
        >
          Copied
        </div>
      </div>
    </div>
  );
});

export default BankTransferModal;
